import apiClient from './apiClient';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('access_token');
    this.userInfo = this.normalizeUserInfo(this.readStoredUserInfo());
    this.currentUserPromise = null;
    this.currentUserCache = null;
    this.currentUserCacheTime = 0;
    // Migrate older sessions that may still contain client-controlled admin flags.
    localStorage.setItem('user_info', JSON.stringify(this.userInfo));
  }

  readStoredUserInfo() {
    try {
      return JSON.parse(localStorage.getItem('user_info') || '{}');
    } catch {
      localStorage.removeItem('user_info');
      return {};
    }
  }

  normalizeUserInfo(info = {}) {
    const { is_admin, ...safeInfo } = info || {};
    return safeInfo;
  }

  // Get Microsoft login URL from backend
  async getMicrosoftLoginUrl() {
    try {
      const response = await apiClient.get('/auth/login-url');
      return response.data.login_url;
    } catch {
      throw new Error('Failed to get login URL');
    }
  }

  // Handle OAuth callback with authorization code
  async handleCallback(code, state) {
    try {
      const response = await apiClient.post('/auth/callback', { code, state });
      const { access_token, id_token, user_email, user_name, is_admin } = response.data;
      
      // Store authentication data
      this.token = id_token;
      this.userInfo = { email: user_email, name: user_name };
      this.currentUserCache = { email: user_email, name: user_name, is_admin };
      this.currentUserCacheTime = Date.now();
      
      localStorage.setItem('access_token', id_token);
      localStorage.setItem('user_info', JSON.stringify(this.userInfo));
      
      return { access_token, id_token, user_email, user_name, is_admin };
    } catch (error) {
      // Clear any partial auth data
      this.logout();
      
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      if (status === 400) {
        throw new Error(detail || 'Invalid authorization code');
      } else if (status === 409) {
        throw new Error(detail || 'Account data conflict');
      } else if (status === 503) {
        throw new Error(detail || 'Database unavailable');
      } else if (status === 500) {
        throw new Error(detail || 'Server error during authentication');
      } else {
        throw new Error(detail || 'Authentication failed. Please try again.');
      }
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token && !!this.userInfo.email;
  }

  // Get current user info
  getUserInfo() {
    return this.userInfo;
  }

  // Update user info
  updateUserInfo(newInfo) {
    this.userInfo = this.normalizeUserInfo({ ...this.userInfo, ...newInfo });
    localStorage.setItem('user_info', JSON.stringify(this.userInfo));
  }

  async getCurrentUser({ force = false } = {}) {
    const cacheIsFresh = Date.now() - this.currentUserCacheTime < 30000;
    if (!force && this.currentUserCache && cacheIsFresh) return this.currentUserCache;
    if (!force && this.currentUserPromise) return this.currentUserPromise;

    this.currentUserPromise = apiClient.get('/auth/me', {
      headers: { Authorization: `Bearer ${this.getToken()}` },
    }).then((response) => {
      const { user_email, user_name, is_admin } = response.data;
      this.userInfo = { email: user_email, name: user_name };
      this.currentUserCache = { email: user_email, name: user_name, is_admin };
      this.currentUserCacheTime = Date.now();
      localStorage.setItem('user_info', JSON.stringify(this.userInfo));
      return this.currentUserCache;
    }).finally(() => {
      this.currentUserPromise = null;
    });

    return this.currentUserPromise;
  }

  // Get access token
  getToken() {
    return this.token;
  }

  // Logout user
  logout() {
    this.token = null;
    this.userInfo = {};
    this.currentUserPromise = null;
    this.currentUserCache = null;
    this.currentUserCacheTime = 0;
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_info');
  }

  // Redirect to Microsoft login
  redirectToMicrosoftLogin() {
    this.getMicrosoftLoginUrl()
      .then(loginUrl => {
        if (!loginUrl) {
          throw new Error('Login URL not available');
        }
        window.location.href = loginUrl;
      })
      .catch(() => {
        this.logout();
      });
  }
}

export default new AuthService();
